/**
 * CR-8227: the divisions and sites of a new organisation, one read-only row
 * each — level, name, where it belongs, country and primary email. Details
 * selects a row for the details panel below the table; sites sit indented
 * under their division.
 */
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { CheckCircle2, Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { LEVEL_COLORS, type SetupDraft, type SetupRow } from './organisationSetup';

interface OrganisationSetupRowsProps {
  draft: SetupDraft;
  disabled: boolean;
  /** The row whose details the panel below shows. */
  selectedKey: string | null;
  onSelect: (key: string) => void;
  onAddDivision: () => void;
  onAddSite: () => void;
}

const GRID = 'grid grid-cols-[110px_minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1.4fr)_96px] items-center gap-3';

/**
 * @param draft - the setup draft
 * @param disabled - true while the create run is going
 * @param selectedKey - the row shown in the details panel
 * @param onSelect - shows a row in the details panel
 * @param onAddDivision - appends a division row
 * @param onAddSite - appends a site row
 */
export function OrganisationSetupRows({
  draft, disabled, selectedKey, onSelect, onAddDivision, onAddSite,
}: OrganisationSetupRowsProps) {
  const { labels, shape, rows } = draft;
  const divisions = rows.filter((row) => row.kind === 'division');
  const sites = rows.filter((row) => row.kind === 'site');
  const groupName = draft.group.name.trim() || `The ${labels.group.toLowerCase() || 'group'}`;
  const nameOf = (row: SetupRow) => row.form.name.trim();
  const belongsTo = (row: SetupRow): string => {
    if (shape === 'single_site') return `No ${labels.group.toLowerCase() || 'group'}`;
    if (shape === 'group_sites' || row.kind === 'division') return groupName;
    const division = divisions.find((d) => d.key === row.parentKey);
    if (!division) return `Choose a ${labels.division.toLowerCase()}`;
    return nameOf(division) || `${labels.division} ${divisions.indexOf(division) + 1}`;
  };
  // Sites listed under their division, then any not yet placed.
  const ordered = shape === 'group_divisions_sites'
    ? [
      ...divisions.flatMap((division) => [division, ...sites.filter((site) => site.parentKey === division.key)]),
      ...sites.filter((site) => !divisions.some((division) => division.key === site.parentKey)),
    ]
    : sites;

  return (
    <div className="overflow-x-auto rounded-md border">
      <div className="min-w-[860px]">
        <div className={cn(GRID, 'bg-muted/60 px-4 py-2.5 text-xs font-medium text-muted-foreground')}>
          <div>Level</div>
          <div>Name *</div>
          <div>Belongs to</div>
          <div>Country</div>
          <div>Primary email *</div>
          <div />
        </div>
        {ordered.map((row) => {
          const isSite = row.kind === 'site';
          const level = isSite ? labels.site : labels.division;
          const indented = isSite && shape === 'group_divisions_sites';
          return (
            <div
              key={row.key}
              className={cn(GRID, 'border-t px-4 py-2.5 text-sm', row.key === selectedKey && 'bg-primary/5')}
            >
              <div>
                <Badge className={cn('text-xs text-white', isSite ? LEVEL_COLORS.site.fill : LEVEL_COLORS.division.fill)}>
                  {level}
                </Badge>
              </div>
              <div className={cn('flex min-w-0 items-center gap-2 font-medium', indented && 'pl-5')}>
                <span className={cn('truncate', !nameOf(row) && 'font-normal italic text-muted-foreground')} title={nameOf(row)}>
                  {nameOf(row) || `New ${level.toLowerCase()}`}
                </span>
                {row.createdId && <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" aria-label="Created" />}
              </div>
              <div className="truncate text-muted-foreground" title={belongsTo(row)}>{belongsTo(row)}</div>
              <div className="truncate text-muted-foreground" title={row.form.country}>{row.form.country}</div>
              <div className="truncate text-muted-foreground" title={isSite ? row.form.primaryEmail : undefined}>
                {isSite ? row.form.primaryEmail : ''}
              </div>
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  onClick={() => onSelect(row.key)}
                  aria-label={`Details of ${nameOf(row) || level}`}
                  aria-pressed={row.key === selectedKey}
                >
                  Details
                </Button>
              </div>
            </div>
          );
        })}
        {shape !== 'single_site' && (
          <div className="flex flex-wrap gap-3 border-t px-4 py-3">
            {shape === 'group_divisions_sites' && (
              <Button
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={onAddDivision}
                className={cn('border-dashed', LEVEL_COLORS.division.add)}
              >
                <Plus className="mr-1 h-4 w-4" /> Add {labels.division.toLowerCase() || 'division'}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={onAddSite}
              className={cn('border-dashed', LEVEL_COLORS.site.add)}
            >
              <Plus className="mr-1 h-4 w-4" /> Add {labels.site.toLowerCase() || 'site'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
